import { BlockBlobClient } from "@azure/storage-blob";

const AZURE_BLOCK_SIZE = 4 * 1024 * 1024; // 4MB block size
const AZURE_CONCURRENCY = 5;

/**
 * GET /v5/assets/token returns blobPath as gs://<bucket>/<path> when it signed a
 * Google Cloud Storage URL, and a plain blob path when it signed an Azure SAS URL.
 */
export const isGcsBlobPath = (blobPath) =>
  typeof blobPath === "string" && blobPath.startsWith("gs://");

const abortError = () => {
  const error = new Error("Upload aborted");
  error.name = "AbortError";
  return error;
};

/**
 * Upload the whole file in one PUT. A GCS signed URL needs no extra headers, and
 * the bucket's CORS policy only allows standard ones such as Content-Type, so the
 * Azure SDK (which adds x-ms-* headers and expects Azure's responses) can't be
 * used. XMLHttpRequest rather than fetch, because fetch can't report upload
 * progress.
 */
const putFile = (uploadUrl, file, { contentType, onProgress, abortSignal }) =>
  new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(abortError());
      return;
    }

    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    const settle = (callback) => {
      abortSignal?.removeEventListener("abort", onAbort);
      callback();
    };

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) =>
      onProgress?.({ loadedBytes: event.loaded });
    xhr.onload = () =>
      settle(() =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`Upload failed with status ${xhr.status}`)),
      );
    xhr.onerror = () =>
      settle(() => reject(new Error("Upload failed: network or CORS error")));
    xhr.onabort = () => settle(() => reject(abortError()));

    abortSignal?.addEventListener("abort", onAbort);
    xhr.send(file);
  });

/**
 * Upload a video to the signed URL from GET /v5/assets/token, whichever store
 * (Azure Blob or Google Cloud Storage) it was signed for.
 * @param {{ uploadUrl: string, blobPath: string }} token - data from the token API
 * @param {File|Blob} file - the video to upload
 * @param {{ contentType?: string, onProgress?: (event: { loadedBytes: number }) => void, abortSignal?: AbortSignal }} [options]
 */
export const uploadToSignedUrl = (
  { uploadUrl, blobPath, storage, provider },
  file,
  { contentType = file.type || "video/mp4", onProgress, abortSignal } = {},
) => {
  const hostname = new URL(uploadUrl).hostname;
  const isGcs =
    isGcsBlobPath(blobPath) ||
    ["gcp", "gcs"].includes(String(storage || provider || "").toLowerCase()) ||
    hostname === "storage.googleapis.com" ||
    hostname.endsWith(".storage.googleapis.com");
  if (isGcs) {
    return putFile(uploadUrl, file, { contentType, onProgress, abortSignal });
  }

  return new BlockBlobClient(uploadUrl).uploadBrowserData(file, {
    blockSize: AZURE_BLOCK_SIZE,
    concurrency: AZURE_CONCURRENCY,
    onProgress,
    abortSignal,
    blobHTTPHeaders: { blobContentType: contentType },
  });
};
